import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtTime, timeAgo } from "@/lib/format";
import { Flame, Check, HandHelping, MonitorPlay, Utensils } from "lucide-react";

function kitchenState(o) {
  if (o.kitchen_status) return o.kitchen_status;
  if (o.status === "preparing") return "cooking";
  if (o.status === "ready") return "ready";
  if (o.status === "served") return "handed";
  return "queued";
}

export default function KitchenPortal() {
  const qc = useQueryClient();
  const { data: orders = [] } = useQuery({
    queryKey: ["kitchen"],
    queryFn: async () => (await api.get("/orders?kitchen=true")).data,
    refetchInterval: 3000,
  });

  const setStatus = async (o, action) => {
    try {
      await api.post(`/orders/${o.id}/kitchen-status`, { action });
      const msg = { cooking: "Started cooking", ready: "Food is ready", handed: "Given to waiter" }[action];
      toast.success(`${o.order_number}: ${msg}`);
      qc.invalidateQueries({ queryKey: ["kitchen"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const ageColor = (iso) => {
    const mins = (Date.now() - new Date(iso).getTime()) / 60000;
    if (mins > 15) return "#FF3B30";
    if (mins > 8) return "#FFCC00";
    return "#34C759";
  };

  return (
    <div className="h-full overflow-y-auto bg-black p-5" data-testid="kitchen-portal">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-white">
          <MonitorPlay className="w-6 h-6 text-[var(--kul-primary)]" />
          <h1 className="text-2xl font-700 tracking-tight">Kitchen Portal</h1>
          <span className="text-[#8e8e93] text-sm ml-2">{orders.length} tickets</span>
        </div>
        <div className="text-[#8e8e93] text-sm">Live · auto-refresh</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {orders.map((o) => {
          const ks = kitchenState(o);
          return (
            <div key={o.id} className="rounded-2xl p-4 flex flex-col" data-testid={`kitchen-ticket-${o.id}`}
              style={{ background: "#1c1c1e", boxShadow: `0 0 0 1.5px ${ageColor(o.created_at)}` }}>
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <Utensils className="w-4 h-4 text-[var(--kul-primary)]" />
                  <span className="font-mono-k font-semibold">{o.order_number}</span>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full text-black font-semibold" style={{ background: ageColor(o.created_at) }}>{timeAgo(o.created_at)}</span>
              </div>
              <div className="text-[11px] text-[#8e8e93] mt-1 capitalize">
                {o.order_type}{o.brand_name ? ` · ${o.brand_name}` : ""}{o.table_name ? ` · Table ${o.table_name}` : o.table_id ? " · table" : ""} · {fmtTime(o.created_at)} · {o.created_by}
              </div>
              {o.pickup_deadline && (() => {
                const left = Math.round((new Date(o.pickup_deadline).getTime() - Date.now()) / 1000);
                const overdue = left < 0;
                const mm = Math.floor(Math.abs(left) / 60);
                const ss = Math.abs(left) % 60;
                return (
                  <div className={`mt-2 text-xs font-mono font-semibold ${overdue ? "text-[#FF3B30]" : "text-[#64D2FF]"}`}>
                    SLA {overdue ? "OVERDUE" : "pickup"} {overdue ? "+" : ""}{mm}:{String(ss).padStart(2, "0")}
                  </div>
                );
              })()}
              <div className="mt-3 space-y-1.5 flex-1">
                {o.items.map((it, i) => (
                  <div key={i}>
                    <div className="text-white text-[15px] font-medium">{it.qty}× {it.name}</div>
                    {it.packaging ? <div className="text-[11px] text-[#64D2FF]">PKG: {it.packaging}</div> : null}
                  </div>
                ))}
              </div>
              {o.packaging_notes && <div className="text-xs text-[#64D2FF] mt-2">Pack: {o.packaging_notes}</div>}
              {o.notes && <div className="text-xs text-[#FFCC00] mt-2">Note: {o.notes}</div>}
              <div className="mt-3 grid gap-2">
                {ks === "queued" && (
                  <button onClick={() => setStatus(o, "cooking")} data-testid={`ks-cooking-${o.id}`}
                    className="w-full py-2.5 rounded-xl bg-[#FF9F0A] text-black font-semibold flex items-center justify-center gap-2">
                    <Flame className="w-4 h-4" /> Started cooking
                  </button>
                )}
                {ks === "cooking" && (
                  <button onClick={() => setStatus(o, "ready")} data-testid={`ks-ready-${o.id}`}
                    className="w-full py-2.5 rounded-xl bg-[#34C759] text-white font-semibold flex items-center justify-center gap-2">
                    <Check className="w-4 h-4" /> Food is ready
                  </button>
                )}
                {ks === "ready" && (
                  <button onClick={() => setStatus(o, "handed")} data-testid={`ks-handed-${o.id}`}
                    className="w-full py-2.5 rounded-xl bg-[var(--kul-primary)] text-white font-semibold flex items-center justify-center gap-2">
                    <HandHelping className="w-4 h-4" /> Given to waiter
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {!orders.length && <div className="text-center text-[#636366] py-32 text-lg">No tickets. Kitchen is clear.</div>}
    </div>
  );
}
